import {constant} from '@/const/const'
import util from '@/mixins/util'

/**
 * 電車の移動であるかどうかを判定
 * @param {String} move 移動の名称
 * @returns 電車の移動であるかどうか
 */
export function isTrainMove(move) {
  return [
    constant.MOVE.SUPEREXPRESS_TRAIN,
    constant.MOVE.SLEEPER_ULTRAEXPRESS,
    constant.MOVE.ULTRAEXPRESS_TRAIN,
    constant.MOVE.EXPRESS_TRAIN,
    constant.MOVE.RAPID_TRAIN,
    constant.MOVE.SEMIEXPRESS_TRAIN,
    constant.MOVE.LOCAL_TRAIN,
  ].includes(move)
}

/**
 * 対象の地点が乗換不要かどうかを判定
 * @param {Object} prevMoveSection 前の移動セクション
 * @param {Object} pointSection 対象の地点セクション
 * @param {Object} nextMoveSection 次の移動セクション
 * @returns 対象の地点が乗換不要かどうか
 */
export function isNoNextTransferRequired(
  prevMoveSection,
  pointSection,
  nextMoveSection
) {
  // 前の移動セクションがない(出発地点)は乗換不要判定の対象外のためfalse
  if (!prevMoveSection) return false
  // 次の移動セクションがない(目的地点)は乗換不要判定の対象外のためfalse
  if (!nextMoveSection) return false
  // 対象の地点セクションが経由駅の場合は乗換不要判定の対象外のためfalse
  if (pointSection.withVia) return false

  // 前後の移動セクションが「電車」である かつ 乗換不要/直通運転の場合はtrue
  return (
    isTrainMove(prevMoveSection.move) &&
    isTrainMove(nextMoveSection.move) &&
    !prevMoveSection.nextTransit
  )
}
/**
 * moveセクションのセクションカラー色を取得
 * @param {Object} moveSection 移動セクション
 * @returns セクションカラー
 */
export function getMobilitySectionColor(moveSection) {
  return moveSection.move == constant.MOBILITY.WALK
    ? constant.COLOR.WALK
    : moveSection.color || constant.COLOR.GENERAL_MOBILITY
}

/**
 * セクションの中から出発時間を取得する
 * @param {Array} sections セクションリスト
 * @return {String} 出発時間 (h:mm形式)
 */
export function getStartTimeFromSection(sections) {
  // 一番最初の「move」セクションを抽出
  const firstSection = sections.find((section) => section.type === 'move')
  // 上記セクションの出発時間を抽出
  return firstSection.fromTime
}

/**
 * セクションの中から到着時間を取得する
 * @param {Array} sections セクションリスト
 * @return {String} 到着時間 (h:mm形式)
 */
export function getGoalTimeFromSection(sections) {
  // 一番最後の「move」セクションを抽出
  const firstSection = sections.findLast((section) => section.type === 'move')
  // 上記セクションの到着時間を抽出
  return firstSection.toTime
}

/**
 * 乗り換え履歴の取得
 * @return {Object} 乗り換え検索履歴
 */
export function getConditionsHistory() {
  return util.methods.getLocalStorage(
    constant.TRANSFER_CONDITIONS_HISTORY_STORAGE_KEY
  )
}

/**
 * 乗り換え検索履歴を登録
 * ※履歴がある場合、先頭に移動する
 * @param {Object} registerHistory 登録用の検索履歴データ
 * @return {Object} 検索履歴登録後の履歴一覧
 */
export function registerTransferConditionsHistory(registerHistory) {
  let conditionHistoryList = structuredClone(getConditionsHistory()) ?? []
  // eslint-disable-next-line no-magic-numbers
  if (conditionHistoryList.length !== 0) {
    // 履歴がある場合、重複したデータを削除
    conditionHistoryList = removeDuplicateData(
      registerHistory,
      conditionHistoryList
    )
  }

  //履歴を追加
  conditionHistoryList.unshift(registerHistory)
  //最新の10件以内になるように限定
  while (constant.MAX_HISTORY_NUM < conditionHistoryList.length) {
    conditionHistoryList.pop()
  }
  util.methods.setLocalStorage(
    constant.TRANSFER_CONDITIONS_HISTORY_STORAGE_KEY,
    conditionHistoryList
  )
  return conditionHistoryList
}

/** 以下はローカルからしか呼び出しなし */

/**
 * @param registerData 検索対象データ
 * @param conditionHistoryList  削除対象データ
 */
export function removeDuplicateData(registerData, conditionHistoryList) {
  // 重複したデータを探す
  return conditionHistoryList.filter((history) => {
    return (
      !isDuplicatedObject(registerData.start, history.start) ||
      !isDuplicatedVia(registerData.via, history.via) ||
      !isDuplicatedObject(registerData.goal, history.goal)
    )
  })
}
/**
 * 引数が同じかを判断
 * @param {Object} registerData
 * @param {Object} historyData
 * @returns
 */
export function isDuplicatedObject(registerData, historyData) {
  return (
    registerData.name == historyData.name &&
    registerData.nodeId == historyData.nodeId &&
    registerData.type == historyData.type
  )
}
/**
 * 経由地が同じデータかを判断
 * @param registerData  登録データ
 * @param historyData 履歴データ
 * @return true：同じデータ
 *         false：異なるデータ
 */
export function isDuplicatedVia(registerVia, historyVia) {
  // 経由地の数が同じかを判断
  let isNotSameLength = registerVia.length !== historyVia.length
  if (isNotSameLength) {
    return false
  }

  // 経由地をひとつずつ比較する
  let result = true
  for (let i = 0; i < registerVia.length; i++) {
    if (!result) {
      // すでにデータが異なる場合は何もせずに処理を抜ける
      break
    }
    result = isDuplicatedObject(registerVia[i], historyVia[i])
  }
  return result
}
